import React, { useEffect, useMemo, useState } from "react";
import { PatientLanguageSelect } from "./PatientLanguageSelect";
import { hasStaticEducation, patientLanguageName } from "../lib/patientLanguages";
import { HeartPulse, Package, ExternalLink, Droplets, Syringe, Wind, Activity, ShieldAlert, Gauge, MessageCircle } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import { EDU_KITS, kitWhatsAppText } from "../lib/eduKits";
import { buildWhatsAppBrandedMessage } from "../lib/clinic";
import { openWhatsAppRaw } from "../lib/whatsapp";

const ICONS = { dkd: Droplets, hf: HeartPulse, insulin: Syringe, inhaler: Wind, thyroid: Activity, anticoag: ShieldAlert, htn: Gauge };
const ACCENT = {
  amber: { border: "border-amber-300", bg: "from-amber-50", chip: "bg-amber-600", icon: "text-amber-700 bg-amber-100", btn: "bg-amber-600 hover:bg-amber-700" },
  rose: { border: "border-rose-300", bg: "from-rose-50", chip: "bg-rose-600", icon: "text-rose-700 bg-rose-100", btn: "bg-rose-600 hover:bg-rose-700" },
  sky: { border: "border-sky-300", bg: "from-sky-50", chip: "bg-sky-600", icon: "text-sky-700 bg-sky-100", btn: "bg-sky-600 hover:bg-sky-700" },
  teal: { border: "border-teal-300", bg: "from-teal-50", chip: "bg-teal-600", icon: "text-teal-700 bg-teal-100", btn: "bg-teal-600 hover:bg-teal-700" },
  violet: { border: "border-violet-300", bg: "from-violet-50", chip: "bg-violet-600", icon: "text-violet-700 bg-violet-100", btn: "bg-violet-600 hover:bg-violet-700" },
  red: { border: "border-red-300", bg: "from-red-50", chip: "bg-red-600", icon: "text-red-700 bg-red-100", btn: "bg-red-600 hover:bg-red-700" },
  emerald: { border: "border-emerald-300", bg: "from-emerald-50", chip: "bg-emerald-600", icon: "text-emerald-700 bg-emerald-100", btn: "bg-emerald-600 hover:bg-emerald-700" },
};

// Auto-detected patient-education kits (DKD / HF / Insulin / Inhaler / …),
// same UX as the GLP-1 companion kit: bilingual + one-tap printable handout
// + branded WhatsApp share (full kit text + secure portal link).
export default function EduKitPanel({ patientId, patient, medicines, ctx, defaultLang = "mr" }) {
  const [lang, setLang] = useState(defaultLang);
  const [busy, setBusy] = useState("");
  const [busyWa, setBusyWa] = useState("");
  useEffect(() => { setLang(defaultLang); }, [defaultLang, patientId]);
  const available = hasStaticEducation(lang);

  const detected = useMemo(
    () => EDU_KITS.map((k) => ({ kit: k, res: k.detect(medicines || [], ctx || {}) })).filter((d) => d.res.matched),
    [medicines, ctx],
  );
  if (detected.length === 0) return null;

  const createKit = (kit, res) => api.post("/edukits", {
    kit_type: kit.id,
    patient_id: patientId,
    language: lang,
    detected_medicines: (res.meds || []).map((m) => ({ brand_name: m.brand_name, generic_name: m.generic_name || m.generic })),
    context: { egfr: ctx?.egfr ?? null },
  });

  const prepare = async (kit, res) => {
    setBusy(kit.id);
    try {
      const r = await createKit(kit, res);
      toast.success(lang === "mr" ? "किट तयार आहे" : "Kit prepared", { description: r.data.kit.kit_id });
      window.open(`/edukit/${r.data.kit.kit_id}`, "_blank");
    } catch (e) {
      console.debug("[silent]", e);
      toast.error("Could not prepare the kit");
    } finally {
      setBusy("");
    }
  };

  // Branded WhatsApp share — clinic header/credentials/contact wrap the FULL
  // kit text, plus a 30-day secure portal link so the patient can reopen it.
  const shareWhatsApp = async (kit, res) => {
    if (!patient?.mobile) { toast.error("No mobile number on file for this patient"); return; }
    setBusyWa(kit.id);
    try {
      await createKit(kit, res);
      let link = "";
      try {
        const pl = await api.post("/portal/link", { patient_id: patientId, expires_hours: 24 * 30, reuse: true });
        link = pl.data?.portal_url || "";
      } catch (e) { console.debug("[silent]", e); }
      const body = kitWhatsAppText(kit, lang) +
        (link ? `\n\n🔗 ${lang === "mr" ? "तुमची संपूर्ण काळजी किट ऑनलाइन उघडा" : "Open your full care kit online"}: ${link}${lang !== "en" ? `?lang=${lang}` : ""}` : "");
      const msg = buildWhatsAppBrandedMessage({
        lang,
        title: kit.title[lang] || kit.title.en,
        body,
        patientName: patient?.name || "",
        kind: "education",
        dateStr: new Date().toLocaleDateString("en-IN"),
      });
      openWhatsAppRaw(patient.mobile, msg);
      toast.success(lang === "mr" ? "WhatsApp उघडत आहे…" : "Opening WhatsApp…");
    } catch (e) {
      console.debug("[silent]", e);
      toast.error("Could not share the kit");
    } finally {
      setBusyWa("");
    }
  };

  return (
    <div className="space-y-2 mb-3" data-testid="edukit-panel">
      {detected.map(({ kit, res }) => {
        const A = ACCENT[kit.accent];
        const Icon = ICONS[kit.id] || Package;
        return (
          <div key={kit.id} className={`rounded-lg border ${A.border} bg-gradient-to-r ${A.bg} via-white to-white p-3`} data-testid={`edukit-card-${kit.id}`}>
            <div className="flex flex-wrap items-start gap-3">
              <div className={`mt-0.5 rounded-full p-2 ${A.icon}`}><Icon size={18} /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-sm text-slate-900">{kit.banner[lang] || kit.banner.en}</div>
                  <div className={`text-[10px] px-1.5 py-0.5 rounded ${A.chip} text-white font-medium uppercase tracking-wider`}>Auto</div>
                </div>
                <div className="text-[11px] text-slate-600 mt-0.5">
                  {(res.meds || []).map((m) => m.brand_name || m.generic_name).filter(Boolean).join(", ") || (lang === "mr" ? "निदानावरून ओळखले" : "Detected from diagnosis")}
                  {" — "}{lang === "mr" ? "छापण्यायोग्य रुग्ण-शिक्षण पत्रक या Rx सोबत द्या." : "attach the printable patient handout with this Rx."}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <PatientLanguageSelect value={lang} onChange={setLang} disabled={!!patient?.preferred_language || !!busy || !!busyWa} testId={`edukit-language-${kit.id}`} />
                {!available && <p data-testid={`edukit-unavailable-${kit.id}`} className="text-xs text-amber-800">Kit unavailable in {patientLanguageName(lang)}. Use Education Hub.</p>}
                <Button size="sm" onClick={() => shareWhatsApp(kit, res)} disabled={!available || !!busy || !!busyWa} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`edukit-whatsapp-${kit.id}`}>
                  <MessageCircle size={14} className="mr-1" />
                  {busyWa === kit.id ? (lang === "mr" ? "पाठवत आहे…" : "Sending…") : "WhatsApp"}
                </Button>
                <Button size="sm" onClick={() => prepare(kit, res)} disabled={!available || !!busy || !!busyWa} className={`${A.btn} text-white`} data-testid={`edukit-prepare-${kit.id}`}>
                  <Package size={14} className="mr-1" />
                  {busy === kit.id ? (lang === "mr" ? "तयार करत आहे…" : "Preparing…") : (lang === "mr" ? "किट तयार करा" : "Prepare Kit")}
                  {busy !== kit.id && <ExternalLink size={11} className="ml-1" />}
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
